// src/components/input/NumericField.tsx
import {
  Controller,
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import { NumericFormat, type NumericFormatProps } from "react-number-format";
import TextField, { type TextFieldProps } from "@mui/material/TextField";
import LabelInput from "./LabelInput";
import { Box, type OutlinedInputProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import { common, input, overview } from "../../i18n";
import { CustomFormHelperText } from "./CustomFormHelperText";

type Mode = "decimal" | "integer" | "currency" | "percent";

type BaseNumericProps = {
  decimalScale?: number;
  fixedDecimalScale?: boolean;
  thousandSeparator?: boolean | string;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  prefix?: string;
  suffix?: string;
  mode?: Mode;

  /** แสดง Label ด้านบน (และซ่อน label ใน TextField) */
  showLabelInput?: boolean;
  label?: string;
  info?: string;
  required?: boolean;
  /** ข้อความเมื่อค่าเกิน max (ค่าเกินจะค้างในช่องและแสดงข้อความ ไม่ถูกตัดทิ้ง) */
  maxMessage?: string;
};

type ExtraTextFieldProps = Omit<
  TextFieldProps,
  "value" | "onChange" | "defaultValue" | "type"
>;

export type NumericFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = BaseNumericProps &
  ExtraTextFieldProps & {
    name: TName;
    control: Control<TFieldValues>;
  };

/* ---------- preset helper ---------- */
function applyPreset(p: BaseNumericProps) {
  const preset = {
    decimalScale: p.decimalScale ?? 2,
    fixedDecimalScale: p.fixedDecimalScale ?? false,
    thousandSeparator: p.thousandSeparator ?? ",",
    allowNegative: p.allowNegative ?? true,
    prefix: p.prefix ?? "",
    suffix: p.suffix ?? "",
  };

  switch (p.mode) {
    case "integer":
      preset.decimalScale = 0;
      preset.fixedDecimalScale = true;
      break;
    case "currency":
      if (p.decimalScale == null) preset.decimalScale = 2;
      if (p.fixedDecimalScale == null) preset.fixedDecimalScale = true;
      if (p.thousandSeparator == null) preset.thousandSeparator = ",";
      break;
    case "percent":
      if (p.decimalScale == null) preset.decimalScale = 2;
      if (p.fixedDecimalScale == null) preset.fixedDecimalScale = true;
      if (!p.suffix) preset.suffix = " %";
      break;
  }
  return preset;
}

/* ---------- RHF version ---------- */
export function NumericField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: NumericFieldProps<TFieldValues, TName>) {
  const {
    name,
    control,
    min,
    max,
    mode,
    decimalScale,
    fixedDecimalScale,
    thousandSeparator,
    allowNegative,
    prefix,
    suffix,
    showLabelInput,
    label,
    info,
    required,
    maxMessage,
    ...textFieldProps
  } = props;

  const preset = applyPreset({
    mode,
    decimalScale,
    fixedDecimalScale,
    thousandSeparator,
    allowNegative,
    prefix,
    suffix,
  });

  const showLabel = showLabelInput === undefined ? true : !!showLabelInput;

  const { t } = useTranslation([common, overview, input], {
    nsMode: "fallback",
  });

  // แยก InputProps ของผู้ใช้มาจัด merge เอง
  const { InputProps: userInputProps, ...restTextFieldProps } =
    (textFieldProps as TextFieldProps) ?? {};

  // base style ของเรา
  const baseSx: OutlinedInputProps["sx"] = {
    "& input": { textAlign: showLabel ? "left" : "right" },
    minWidth: 140,
    bgcolor: restTextFieldProps.disabled
      ? "var(--gray5) !important"
      : undefined,
    my: "8px",
    height: "40px",
  };

  // ✅ ลำดับ merge ที่ถูกต้อง: base → user overrides
  const mergedInputProps: Partial<OutlinedInputProps> = {
    inputProps: {
      autoComplete: "off",
      inputMode: "decimal",
      ...(userInputProps?.inputProps ?? {}),
    },
    sx: {
      ...baseSx,
      ...(userInputProps?.sx as any),
    },
    // เอาของผู้ใช้มาท้ายสุด (เช่น endAdornment/startAdornment)
    ...userInputProps,
  };

  return (
    <Box
      sx={{
        width: "100%",
        ".Mui-disabled": { background: "transparent" },
      }}
    >
      {showLabel && (
        <LabelInput
          label={label ?? ""}
          info={label === info ? undefined : info ?? undefined}
          required={required}
        />
      )}

      <Controller
        name={name}
        control={control}
        rules={{
          required:
            !textFieldProps.disabled && required
              ? (t("This-field-is-required") as string)
              : false,
          validate: (value) =>
            textFieldProps.disabled ||
            max == null ||
            value == null ||
            value <= max ||
            (maxMessage ?? (t("Value is too large") as string)),
        }}
        render={({ field, fieldState }) => (
          <>
            <NumericFormat
              customInput={TextField}
              // ----- format props -----
              thousandSeparator={preset.thousandSeparator}
              decimalScale={preset.decimalScale}
              fixedDecimalScale={preset.fixedDecimalScale}
              allowNegative={preset.allowNegative}
              prefix={preset.prefix}
              suffix={preset.suffix}
              // ----- value mapping -----
              value={field.value ?? ""} // UI ว่าง = ""
              onValueChange={(v) => field.onChange(v.floatValue ?? null)} // ฟอร์มเก็บ number|null
              onBlur={field.onBlur}
              onFocus={(event) => {
                // select ทั้งหมดเมื่อโฟกัส
                requestAnimationFrame(() => {
                  (event.target as HTMLInputElement)?.select?.();
                });
              }}
              isAllowed={(vals) => {
                const n = vals.floatValue;
                if (n == null) return true;
                if (min != null && n < min) return false;
                return true;
              }}
              // ----- text field props -----
              InputProps={mergedInputProps}
              // ถ้ามี label ด้านบนแล้ว ให้ไม่ขึ้น label ใน input
              label={showLabel ? undefined : label}
              error={!!fieldState.error || restTextFieldProps.error}
              // helperText={
              //   fieldState.error?.message ?? restTextFieldProps.helperText
              // }
              {...(restTextFieldProps as NumericFormatProps<TextFieldProps>)}
            />
            <CustomFormHelperText error={fieldState.error} />
          </>
        )}
      />
    </Box>
  );
}

export default NumericField;
